// StartAssessmentDialogV2.jsx - Enhanced with Autopilot/Co-pilot Mode Selection

import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Modal, Stack, Group, Text, Button, Card, Badge, 
  Select, Divider, Alert, Title, Grid 
} from '@mantine/core';
import { 
  IconRobot, IconSettings, IconRocket, IconInfoCircle
} from '@tabler/icons-react';
import { setModelId } from './redux/actions/riskActions';

const StartAssessmentDialogV2 = ({ open, onClose, preselectedModel = null }) => {
  const allModels = useSelector((state) => state.risks.models);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedModel, setSelectedModel] = useState(preselectedModel);
  const [selectedMode, setSelectedMode] = useState('autopilot'); // Default to Autopilot
  const [isStarting, setIsStarting] = useState(false);

  // Update selectedModel when preselectedModel changes
  React.useEffect(() => {
    setSelectedModel(preselectedModel);
  }, [preselectedModel]);

  const modelsNotStarted = useMemo(() => {
    return Object.entries(allModels)
      .filter(([, model]) => model.assessmentStatus === 'Not Started' || !model.assessmentStatus)
      .map(([modelId, model]) => ({ ...model, modelId }));
  }, [allModels]);

  const handleStartAssessment = async () => {
    if (!selectedModel) return;

    setIsStarting(true);
    dispatch(setModelId(selectedModel.modelId, selectedModel.modelName));

    try {
      let assessmentUuid;

      // Check if an assessment already exists
      const response = await fetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({ modelId: selectedModel.modelId, operation: 'fetch' }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        assessmentUuid = result.assessmentUuid;

        // Update assessment status to 'On-Going'
        await fetch('/.netlify/functions/manageAssessment', {
          method: 'POST',
          body: JSON.stringify({
            modelId: selectedModel.modelId,
            assessmentUuid,
            operation: 'update',
            assessmentStatus: 'On-Going',
          }),
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        // Create a new assessment
        const createResponse = await fetch('/.netlify/functions/manageAssessment', {
          method: 'POST',
          body: JSON.stringify({
            modelId: selectedModel.modelId,
            operation: 'update',
            currentStep: selectedMode === 'autopilot' ? 'autopilot' : 'contextAssessment',
            assessmentStatus: 'On-Going',
          }),
          headers: { 'Content-Type': 'application/json' },
        });

        if (!createResponse.ok) {
          throw new Error('Failed to create a new assessment.');
        }

        const createResult = await createResponse.json();
        assessmentUuid = createResult.assessmentUuid;
      }

      // Close the dialog
      onClose();

      // Navigate based on selected mode
      if (selectedMode === 'autopilot') {
        navigate(`/autopilot-assessment/${selectedModel.modelId}/${assessmentUuid}`);
      } else {
        navigate(`/risk-form/${selectedModel.modelId}/${encodeURIComponent(selectedModel.modelName)}/${assessmentUuid}`);
      }
    } catch (error) {
      console.error('Error starting assessment:', error);
      // Optionally, handle the error (e.g., show a notification)
    } finally {
      setIsStarting(false);
    }
  };

  const modelOptions = modelsNotStarted.map(model => ({
    value: model.modelId,
    label: `${model.modelName || model.modelId} (${model.modelType || 'Unknown Type'})`
  }));

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <Group gap="md" align="center">
          <IconRocket size={24} color="#1976d2" />
          <Title order={3}>Start New Risk Assessment</Title>
        </Group>
      }
      size="xl"
      centered
    >
      <Stack gap="xl">
        {/* Model Selection */}
        <Stack gap="md">
          <Text fw={600} size="md">1. Select Your Model</Text>
          <Select
            placeholder="Choose a model to assess..."
            data={modelOptions}
            value={selectedModel?.modelId || ''}
            onChange={(value) => {
              const model = modelsNotStarted.find(m => m.modelId === value);
              setSelectedModel(model);
            }}
            searchable
            size="md"
          />
          {modelsNotStarted.length === 0 && (
            <Alert color="blue" icon={<IconInfoCircle size="1rem" />}>
              No models available for new assessments. All models have ongoing or completed assessments.
            </Alert>
          )}
        </Stack>

        <Divider />

        {/* Mode Selection - Simplified */}
        <Stack gap="md">
          <Text fw={600} size="md">Assessment Mode</Text>

          <Grid>
            {/* Autopilot Mode */}
            <Grid.Col span={6}>
              <Card
                withBorder
                radius="md"
                p="lg"
                style={{
                  cursor: 'pointer',
                  border: selectedMode === 'autopilot' ? '2px solid #1976d2' : '1px solid #dee2e6',
                  backgroundColor: selectedMode === 'autopilot' ? '#f0f7ff' : 'white'
                }}
                onClick={() => setSelectedMode('autopilot')}
              >
                <Stack gap="md" align="center">
                  <IconRobot size={32} color="#1976d2" />
                  <Text fw={700} size="lg" c="blue">Autopilot</Text>
                  <Text size="sm" ta="center" c="dimmed">
                    AI handles everything automatically
                  </Text>
                  
                  {selectedMode === 'autopilot' ? (
                    <Badge color="blue" variant="light" fullWidth>
                      ✓ Selected
                    </Badge>
                  ) : (
                    <Button variant="light" color="blue" fullWidth size="sm">
                      Select
                    </Button>
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            {/* Co-pilot Mode */}
            <Grid.Col span={6}>
              <Card
                withBorder
                radius="md"
                p="lg"
                style={{
                  cursor: 'pointer',
                  border: selectedMode === 'copilot' ? '2px solid #495057' : '1px solid #dee2e6',
                  backgroundColor: selectedMode === 'copilot' ? '#f8f9fa' : 'white'
                }}
                onClick={() => setSelectedMode('copilot')}
              >
                <Stack gap="md" align="center">
                  <IconSettings size={32} color="#495057" />
                  <Text fw={700} size="lg" c="dark">Co-pilot</Text>
                  <Text size="sm" ta="center" c="dimmed">
                    You guide with AI help
                  </Text>
                  
                  {selectedMode === 'copilot' ? (
                    <Badge color="dark" variant="light" fullWidth>
                      ✓ Selected
                    </Badge>
                  ) : (
                    <Button variant="light" color="dark" fullWidth size="sm">
                      Select
                    </Button>
                  )}
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Stack>

        <Divider />

        {/* Action Buttons */}
        <Group justify="space-between">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          
          <Button
            onClick={handleStartAssessment}
            disabled={!selectedModel || isStarting}
            loading={isStarting}
            leftSection={selectedMode === 'autopilot' ? <IconRobot size={16} /> : <IconSettings size={16} />}
            size="md"
            color={selectedMode === 'autopilot' ? 'blue' : 'dark'}
          >
            {isStarting 
              ? 'Starting Assessment...' 
              : `Start ${selectedMode === 'autopilot' ? 'Autopilot' : 'Co-pilot'} Assessment`
            }
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StartAssessmentDialogV2;